import {
  ENERGY_AND_WATER_DETAIL_ROUTE_NAME,
  ENERGY_DETAIL_ROUTE_NAME,
  WASTEWATER_DETAIL_ROUTE_NAME,
  WATER_DETAIL_ROUTE_NAME
} from '@/data/routes';
import {
  allTechMeasuresProjectsCards,
  allTechProjectsInfo,
  quickWinsProjectsCards,
  topTechMeasuresProjectsCards
} from '@/data/techAndMeasures/index';
import { transformPropertyToIndex } from '@/helpers/techMeasures';
import { useTechMeasuresStore } from '@/store/techMeasures';
import type {
  MeasureFilterData,
  RecommendationsCollapsable,
  TechMeasureRecommendation,
  TechMeasuresProjectCard
} from '@/types';
import type { ComputedRef, Ref } from 'vue';
import { tm } from '@/locales/i18n';

//private logic
const hasTechMeasureProperty = (
  filterProperty: string[],
  measureProperty?: string | string[],
  isRecommendation?: boolean
) => {
  const noMeasureDataAndNoFilterData =
    !measureProperty && !isRecommendation ? !filterProperty.length : true;

  const measureStringDataAsFilterData =
    measureProperty && typeof measureProperty === 'string' && filterProperty.length
      ? filterProperty.indexOf(transformPropertyToIndex(measureProperty)) > -1
      : true;

  const measureArrayDataAsFilterData =
    measureProperty && Array.isArray(measureProperty) && filterProperty.length
      ? measureProperty.some(
          (propertyOption) => filterProperty.indexOf(transformPropertyToIndex(propertyOption)) > -1
        )
      : true;

  return (
    noMeasureDataAndNoFilterData && measureStringDataAsFilterData && measureArrayDataAsFilterData
  );
};

const applySelectedFilters = (
  project: TechMeasuresProjectCard | TechMeasureRecommendation,
  measureFiltersData: MeasureFilterData,
  isRecommendation?: boolean
) =>
  hasTechMeasureProperty(measureFiltersData.category, project.category) &&
  hasTechMeasureProperty(measureFiltersData.etpType, project.etpType) &&
  hasTechMeasureProperty(measureFiltersData.impact, project.impact, isRecommendation) &&
  hasTechMeasureProperty(measureFiltersData.parameter, project.parameter) &&
  hasTechMeasureProperty(
    measureFiltersData.expenditureInvestement,
    project.estimatedInvestmentExpenditureSumup
  ) &&
  hasTechMeasureProperty(measureFiltersData.type, project.typeId);

const filterProjectsByManufacturingProcess = (
  manufacturingProcess: string,
  project: TechMeasuresProjectCard | TechMeasureRecommendation
) =>
  manufacturingProcess.length === 0 ||
  project.manufacturingProcess.indexOf(manufacturingProcess) > -1;

const filterProjectsByMeasureType = (selectedType: string | undefined, currentType: string) =>
  selectedType && selectedType.length > 0
    ? getRelatedMeasuresTypes(selectedType).indexOf(currentType) > -1
    : true;

const filterProjectsByTypeAndManufacturing = (
  projectList: TechMeasuresProjectCard[] | TechMeasureRecommendation[] | Ref<TechMeasuresProjectCard[]> | ComputedRef<TechMeasuresProjectCard[]>,
  selectedType?: string,
  isRecommendation?: boolean
) => {
  const { measureFiltersData, manufacturingProcess } = useTechMeasuresStore();

  const projects = Array.isArray(projectList) ? projectList : projectList.value;

  return projects.filter(
    (project) =>
      filterProjectsByMeasureType(selectedType, project.typeId) &&
      filterProjectsByManufacturingProcess(manufacturingProcess, project) &&
      applySelectedFilters(project, measureFiltersData, isRecommendation)
  );
};


const getRelatedMeasuresTypes = (selectedType: string) => {
  let relatedMeasuresTypes = new Array<string>();

  switch (selectedType) {
    case WASTEWATER_DETAIL_ROUTE_NAME:
      relatedMeasuresTypes = [WASTEWATER_DETAIL_ROUTE_NAME];
      break;
    case ENERGY_AND_WATER_DETAIL_ROUTE_NAME:
    case WATER_DETAIL_ROUTE_NAME:
    case ENERGY_DETAIL_ROUTE_NAME:
      relatedMeasuresTypes = [
        ENERGY_AND_WATER_DETAIL_ROUTE_NAME,
        WATER_DETAIL_ROUTE_NAME,
        ENERGY_DETAIL_ROUTE_NAME
      ];
      break;
    default:
      relatedMeasuresTypes = [];
  }

  return relatedMeasuresTypes;
};

//public methods
export const filterAllProjects = (selectedType?: string) =>
  filterProjectsByTypeAndManufacturing(
    allTechMeasuresProjectsCards.value,
    selectedType
  ) as TechMeasuresProjectCard[];

export const filterProjectsBySelectedTab = (typeParams: string | undefined, tabType: string) =>
  filterProjectsByMeasureType(typeParams, tabType);

export const filterTopProjects = (selectedType?: string) =>
  filterProjectsByTypeAndManufacturing(
    topTechMeasuresProjectsCards.value,
    selectedType
  ) as TechMeasuresProjectCard[];

export const filterQuickWins = (selectedType?: string) =>
  filterProjectsByTypeAndManufacturing(
    quickWinsProjectsCards.value,
    selectedType
  ) as TechMeasuresProjectCard[];

export const getCategoryFilters = (selectedType?: string) => {
  const measureCategoriesOptions = [
    ...new Set(allTechMeasuresProjectsCards.value.map((measure) => measure.category))
  ].sort();

  const recommendationCategoriesOptions = [
    ...new Set(tm('allRecommendations').map((recommendation) => recommendation.category).flat())
  ].sort();

  let calculatedCategoriesOptions = [];

  switch (selectedType) {
    case ENERGY_AND_WATER_DETAIL_ROUTE_NAME:
      calculatedCategoriesOptions = measureCategoriesOptions;
      break;
    case WASTEWATER_DETAIL_ROUTE_NAME:
      calculatedCategoriesOptions = measureCategoriesOptions
        .splice(0, 2)
        .concat(recommendationCategoriesOptions)
        .sort();
      break;
    default:
      calculatedCategoriesOptions = measureCategoriesOptions
        .concat(recommendationCategoriesOptions)
        .sort();
      break;
  }

  return calculatedCategoriesOptions.map((option) => ({
    index: transformPropertyToIndex(option ?? ''),
    value: option ?? ''
  }));
};

export const getProjectDetail = (measureId: string) =>
  allTechProjectsInfo.value.find((project) => project.id === measureId) || null;

export const groupRecommendations = (
  selectedType: string | undefined
): RecommendationsCollapsable[] => {
  const filteredRecommendations = filterProjectsByTypeAndManufacturing(
    tm('allRecommendations'),
    selectedType,
    true
  ) as TechMeasureRecommendation[];

  const allAgrupations: string[] = filteredRecommendations
    .map((recommendation) => recommendation.category)
    .flat();

  const availableCategories: string[] = allAgrupations.filter(
    (category, index) => allAgrupations.indexOf(category) === index
  );

  const groupedRecommendations: RecommendationsCollapsable[] = availableCategories.map(
    (category) => ({
      title: category,
      recommendations: new Array<TechMeasureRecommendation>(),
      typeId: ''
    })
  );

  filteredRecommendations.forEach((recommendation) => {
    recommendation.category.forEach((category) => {
      const currentGroup = groupedRecommendations.find((group) => group.title === category);

      if (currentGroup) {
        currentGroup.recommendations.push(recommendation);
        currentGroup.typeId =
          currentGroup.typeId.length > 0 ? currentGroup.typeId : recommendation.typeId;
      }
    });
  });

  return groupedRecommendations.sort((recommendationA, recommendationB) =>
    recommendationA.title.localeCompare(recommendationB.title)
  );
};

export const getTabAmount = (selectedType: string) =>
  filterProjectsByTypeAndManufacturing(allTechMeasuresProjectsCards.value, selectedType).length;
